import { t } from "@lingui/macro"
import { makeAutoObservable, runInAction } from "mobx"
import React from "react"

import { AlertsService } from "src/api"
import { adminv3_AlertResponse } from "src/api/models/adminv3_AlertResponse"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { FormFields } from "src/lib/form-fields"
import { reportError, reportSuccess } from "src/lib/report"
import { AlertFormFields } from "src/modals/alert-detail/types"
import { ALERT_LEVELS, AlertLevel } from "src/views/alerts/constants"
import { Repository } from "src/types/channel"
import { EModalMode } from "src/types/modal/modal"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"

export class AlertDetailModalStore {
    static Context = React.createContext<AlertDetailModalStore | null>(null)

    isLoading = false
    isReadOnly = false
    formFields: FormFields<AlertFormFields>
    selectedSegments: number[] = []
    originalSegments: number[] = []

    private id: number | null = null
    private mode: EModalMode | "New" = "New"
    private accessGroupName: string = ""

    get isSegmentsDirty() {
        if (this.selectedSegments.length !== this.originalSegments.length)
            return true
        for (let i = 0; i < this.selectedSegments.length; i++) {
            if (this.selectedSegments[i] !== this.originalSegments[i])
                return true
        }
        return false
    }

    constructor() {
        this.formFields = new FormFields<AlertFormFields>({
            title: "",
            text: "",
            cta_label: "",
            cta_url: "",
            access_group_id: DEFAULT_ACCESS_GROUP.id,
            level: ALERT_LEVELS.INFO,
        })

        makeAutoObservable(this)
    }

    async init(
        id: number | null,
        accessGroupId: number,
        mode?: EModalMode,
        accessGroupName?: string,
    ) {
        this.id = id
        this.mode = mode ?? (id != null ? EModalMode.Edit : "New")
        this.isReadOnly = this.mode === EModalMode.View
        this.accessGroupName = accessGroupName ?? ""

        let loadedResponse: adminv3_AlertResponse | undefined
        if (this.id != null) {
            loadedResponse = await this.loadAlert(this.id)
        } else {
            this.formFields.set("access_group_id", accessGroupId)
            this.formFields.set("level", ALERT_LEVELS.INFO)
        }

        if (this.mode === EModalMode.Copy) {
            this.isReadOnly = false
            // Copy access group and segments only when source has WRITE access.
            // If not WRITE (e.g., READ or undefined), clear access group and segments.
            const sourceAccessType = loadedResponse?.access_type as
                | "WRITE"
                | "READ"
                | string
                | undefined
            if (sourceAccessType !== "WRITE") {
                this.formFields.set("access_group_id", DEFAULT_ACCESS_GROUP.id)
                this.selectedSegments = []
                this.originalSegments = []
            }
        }
    }

    private async loadAlert(
        id: number,
    ): Promise<adminv3_AlertResponse | undefined> {
        this.isLoading = true
        try {
            const response = (await AlertsService.getV3AdminAlert({
                id,
            })) as adminv3_AlertResponse
            runInAction(() => this.setFormFromResponse(response))
            return response
        } catch (error) {
            reportError(t`alerts-modal.load-fail`, error)
        } finally {
            runInAction(() => (this.isLoading = false))
        }
        return
    }

    private setFormFromResponse(response: adminv3_AlertResponse) {
        this.formFields.init({
            title: response.title ?? "",
            text: response.text ?? "",
            cta_label: response.cta_label ?? "",
            cta_url: response.cta_url ?? "",
            access_group_id:
                response.access_group_id ?? DEFAULT_ACCESS_GROUP.id,
            level: (response.level as AlertLevel) ?? ALERT_LEVELS.INFO,
        })
        if (Array.isArray(response.segment_ids)) {
            this.selectedSegments = response.segment_ids
            this.originalSegments = response.segment_ids.slice()
        } else {
            this.selectedSegments = []
            this.originalSegments = []
        }
    }

    private getAccessGroupName(_accessGroupId: number): string {
        return this.accessGroupName
    }

    private trackAlertCreated() {
        try {
            const title = this.formFields.get("title") ?? ""
            const message = this.formFields.get("text") ?? ""
            const level = this.formFields.get("level") ?? ALERT_LEVELS.INFO
            const buttonLabel = this.formFields.get("cta_label") ?? ""
            const link = this.formFields.get("cta_url") ?? ""
            const accessGroupId =
                this.formFields.get("access_group_id") ??
                DEFAULT_ACCESS_GROUP.id
            const accessGroupName = this.getAccessGroupName(accessGroupId)

            trackModuleEvent("Alert | Save", {
                Title: title,
                Message: message,
                Level: level,
                "Button label": buttonLabel,
                Link: link,
                "Access group": `${accessGroupName} (${accessGroupId})`,
                Segment:
                    this.selectedSegments.length > 0
                        ? this.selectedSegments.join(", ")
                        : "No segments selected",
            })
        } catch (error) {
            // Failed to track alert created event
        }
    }

    async handleSubmit(): Promise<boolean> {
        if (this.isLoading) {
            return false
        }
        // Validate required fields
        this.formFields.clearErrors()
        const title = this.formFields.get("title")
        if (title == null || title.trim() === "") {
            this.formFields.setError("title", t`errors.required`)
        }
        const text = this.formFields.get("text")
        if (text == null || text.trim() === "") {
            this.formFields.setError("text", t`errors.required`)
        }
        const accessGroupId = this.formFields.get("access_group_id")
        if (
            accessGroupId == null ||
            accessGroupId === DEFAULT_ACCESS_GROUP.id
        ) {
            this.formFields.setError("access_group_id", t`errors.required`)
        }

        // Validate URL format if cta_url is provided
        const ctaUrl = this.formFields.get("cta_url")
        if (ctaUrl != null && ctaUrl.trim() !== "") {
            const urlRegex = /^https?:\/\/.+/i
            if (!urlRegex.test(ctaUrl.trim())) {
                this.formFields.setError("cta_url", t`errors.invalid-url`)
            }
        }

        if (this.formFields.hasErrors()) {
            return false
        }

        // Track alert created event only for new alerts, not edits
        if (this.mode !== EModalMode.Edit) {
            this.trackAlertCreated()
        }

        this.isLoading = true

        try {
            // Prepare request data, converting empty CTA fields to undefined
            const requestData = {
                ...this.formFields.data,
                cta_label:
                    this.formFields.get("cta_label")?.trim() !== ""
                        ? this.formFields.get("cta_label")?.trim()
                        : undefined,
                cta_url:
                    this.formFields.get("cta_url")?.trim() !== ""
                        ? this.formFields.get("cta_url")?.trim()
                        : undefined,
            }

            // If access group is not selected, omit it from the request
            if (
                this.formFields.get("access_group_id") ===
                DEFAULT_ACCESS_GROUP.id
            ) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete (requestData as Record<string, unknown>)[
                    "access_group_id"
                ]
            }

            let action: "create" | "update"
            let alertId: number | null = null
            if (this.mode === EModalMode.Edit && this.id != null) {
                action = "update"
                await AlertsService.putV3AdminAlert({
                    id: this.id,
                    request: requestData,
                })
                alertId = this.id
                reportSuccess(t`alerts-modal.update-success`)
            } else {
                action = "create"
                const response = await AlertsService.postV3AdminAlert({
                    request: requestData,
                })
                alertId = response.id ?? null
                reportSuccess(t`alerts-modal.create-success`)
            }
            // Publish segments if alertId exists and either:
            // - There are segments to publish, OR
            // - We're editing and segments have changed (including removal of all segments)
            if (
                alertId != null &&
                (this.selectedSegments.length > 0 ||
                    (this.mode === EModalMode.Edit && this.isSegmentsDirty))
            ) {
                try {
                    await AlertsService.putV3AdminAlertPublish({
                        id: alertId,
                        request: { segment_ids: this.selectedSegments },
                    })
                    reportSuccess(t`alerts-modal.publish-success`)
                } catch (publishError) {
                    reportError(t`alerts-modal.publish-fail`, publishError)
                }
            }
            Channel.send({
                name: "repository/updated",
                payload: { repository: "alert" as Repository, action },
            })
            return true
        } catch (error) {
            this.formFields.setError("generic", t`alerts-modal.save-fail`)
            reportError(t`alerts-modal.save-fail`, error)
            return false
        } finally {
            runInAction(() => (this.isLoading = false))
        }
    }
}
